(() => {
  if (window.__cloudSearchCollectionsInit) return;
  window.__cloudSearchCollectionsInit = true;

  const descCache = new Map();
  const inFlight = new Map();
  let debounceTimer = null;
  let isMutating = false;

  function getDetailDd(root, dt) {
    const id = dt.getAttribute('data-id');
    if (id) return root.querySelector(`dd[data-id="${id}"]`);
    return dt.nextElementSibling?.matches('dd') ? dt.nextElementSibling : null;
  }

  function getCollectionHandle(row) {
    const link = row.matches('a[href*="/collections/"]')
      ? row
      : row.querySelector('a[href*="/collections/"]');
    if (!link) return null;
    const m = link.getAttribute('href').match(/\/collections\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }

  function normalizeText(el) {
    if (!el) return '';
    return el.textContent.replace(/\s+/g, ' ').trim();
  }

  function fetchCollectionDescription(handle) {
    if (descCache.has(handle)) return Promise.resolve(descCache.get(handle));
    if (inFlight.has(handle)) return inFlight.get(handle);

    const req = fetch(`/collections/${encodeURIComponent(handle)}?section_id=main-collection-banner`)
      .then((r) => (r.ok ? r.text() : ''))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const text = normalizeText(doc.querySelector('.collection-hero__description'));
        descCache.set(handle, text);
        return text;
      })
      .catch(() => {
        descCache.set(handle, '');
        return '';
      })
      .finally(() => inFlight.delete(handle));

    inFlight.set(handle, req);
    return req;
  }

  function getCollectionLink(details) {
    return details.querySelector('a[href*="/collections/"], .cs-item-title, a[id$="-name"]');
  }

  function removeStrayDescriptions(details, link) {
    details.querySelectorAll('.description, .cs-item-description').forEach((el) => {
      if (!link || !link.contains(el)) el.remove();
    });
  }

  function placeDescriptionInDetails(details, text) {
    removeStrayDescriptions(details, null);

    let desc = details.querySelector(':scope > .cs-item-description');
    if (!text) {
      desc?.remove();
      return;
    }

    if (!desc) {
      desc = document.createElement('div');
      desc.className = 'cs-item-description';
      details.appendChild(desc);
    }

    if (desc.textContent === text) return;
    isMutating = true;
    desc.textContent = text;
    isMutating = false;
  }

  function placeDescription(details, text) {
    const link = getCollectionLink(details);
    if (!link) return;

    removeStrayDescriptions(details, link);

    let desc = link.querySelector('.cs-item-description');
    if (!text) {
      desc?.remove();
      return;
    }

    if (!desc) {
      desc = document.createElement('span');
      desc.className = 'cs-item-description';
      link.appendChild(desc);
    }

    if (desc.textContent === text) return;
    isMutating = true;
    desc.textContent = text;
    wrapTitleLine(link);
    isMutating = false;
  }

  function wrapTitleLine(link) {
    if (link.querySelector('.cs-item-title-line')) return;

    const desc = link.querySelector('.cs-item-description');
    const line = document.createElement('span');
    line.className = 'cs-item-title-line';

    const nodes = [...link.childNodes].filter((node) => node !== desc);
    if (!nodes.length) return;

    isMutating = true;
    nodes.forEach((node) => line.appendChild(node));
    link.insertBefore(line, desc || null);
    isMutating = false;
  }

  function mergeCollectionTitleLinks(details) {
    const links = [...details.querySelectorAll(':scope > a[href]')];
    if (links.length <= 1) return;

    const primary = links[0];
    const fragment = document.createDocumentFragment();

    links.forEach((link, index) => {
      if (index > 0) fragment.appendChild(document.createTextNode(' '));
      while (link.firstChild) fragment.appendChild(link.firstChild);
    });

    isMutating = true;
    links.slice(1).forEach((link) => link.remove());
    primary.textContent = '';
    primary.appendChild(fragment);
    isMutating = false;
  }

  function syncCollectionRow(root, row) {
    const details = row.querySelector('.cs-item-details');
    if (!details) return;

    const isMobileAnchor = row.matches('a.cs-item');

    if (!isMobileAnchor) {
      mergeCollectionTitleLinks(details);

      const link = getCollectionLink(details);
      if (link) wrapTitleLine(link);
    }

    const dd = row.matches('dt') ? getDetailDd(root, row) : null;
    let text = normalizeText(dd?.querySelector('.description'));

    if (!text) {
      text = normalizeText(details.querySelector(':scope > .description'));
    }

    const apply = (value) => {
      if (isMobileAnchor) placeDescriptionInDetails(details, value);
      else placeDescription(details, value);
    };

    if (text) {
      apply(text);
      return;
    }

    const handle = getCollectionHandle(row);
    if (!handle) {
      apply('');
      return;
    }

    if (descCache.has(handle)) {
      apply(descCache.get(handle));
      return;
    }

    fetchCollectionDescription(handle).then((fetched) => {
      if (!root.contains(row)) return;
      apply(fetched);
    });
  }

  function syncCollections(root) {
    const block = root.querySelector('.results-block.block-categories');
    if (!block) return;
    block.querySelectorAll('dt.cs-item, a.cs-item').forEach((row) => syncCollectionRow(root, row));
  }

  function scheduleSync(root) {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      syncCollections(root);
    }, 100);
  }

  function bind(root) {
    if (!root || root.dataset.collectionsBound) return;
    root.dataset.collectionsBound = 'true';

    syncCollections(root);

    const block = root.querySelector('.results-block.block-categories') || root;
    new MutationObserver(() => {
      if (isMutating) return;
      scheduleSync(root);
    }).observe(block, { childList: true, subtree: true });
  }

  function init() {
    document.querySelectorAll('#cloud-search-results, .cloud-search-results').forEach(bind);

    new MutationObserver(() => {
      document.querySelectorAll('#cloud-search-results, .cloud-search-results').forEach((el) => {
        if (!el.dataset.collectionsBound) bind(el);
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
